import { fetchApi } from "~vue/api";

/**
 * Matches the plain `filename=` parameter of a Content-Disposition header,
 * quoted (group 1) or bare (group 2, stopping at the next `;`).
 *
 * It deliberately does not match the RFC 5987 `filename*=UTF-8''...` form, which
 * is percent-encoded and would need decoding before it could be used as a name.
 * coach_api sends both, so in `attachment; filename="report.pdf"; filename*=UTF-8''report.pdf`
 * this picks `report.pdf` out of the plain parameter and ignores the encoded one.
 */
const CONTENT_DISPOSITION_FILENAME = /(?:^|;)\s*filename="([^"]*)"|(?:^|;)\s*filename=([^;]+)/;

/**
 * The filename a Content-Disposition header asks for, or undefined when it names none.
 *
 * Undefined rather than an empty string on a header like `filename=""`: assigning
 * "" to link.download makes the browser invent a name, which would silently beat
 * the caller's fallback.
 */
export function filenameFromContentDisposition(header: string | null): string | undefined {
    const match = header?.match(CONTENT_DISPOSITION_FILENAME);
    const filename = (match?.[1] ?? match?.[2])?.trim();
    return filename || undefined;
}

/**
 * Fetch a file from coach_api and hand it to the browser as a download.
 *
 * Exports are authenticated with a bearer token, so a plain link to the endpoint
 * would 401 — the response has to be fetched and turned into a blob URL.
 *
 * Returns false when the request failed, so callers can surface an error.
 */
export async function downloadApiFile(path: string, fallbackFilename: string): Promise<boolean> {
    const response = await fetchApi(path);
    if (!response.ok) {
        return false;
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = filenameFromContentDisposition(response.headers.get("Content-Disposition")) ?? fallbackFilename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return true;
}
